// src/crm/pages/MsaPoEntitySelection.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BASE_URL from '../../url';
import { 
  LuFileText, 
  LuBuilding2, 
  LuShieldCheck, 
  LuGlobe, 
  LuBriefcase, 
  LuArrowRight, 
  LuFileCheck
} from 'react-icons/lu';
import './MsaPo.css';

const DEFAULT_THEMES = {
  '4': {
    themeClass: 'card-prophecy',
    badge: 'Primary US Entity',
    description: 'Direct client master service agreements, primary staffing agency MSAs, and prime client purchase orders.',
    icon: <LuShieldCheck size={24} />,
    accentColor: '#3b5bfd'
  },
  '5': {
    themeClass: 'card-offshore',
    badge: 'Offshore Delivery',
    description: 'Global offshore delivery centers, cross-border development master agreements, and subcontractor work orders.',
    icon: <LuBuilding2 size={24} />,
    accentColor: '#0284c7'
  },
  '6': {
    themeClass: 'card-cognifyar',
    badge: 'Technology Entity',
    description: 'Specialized technology agreements, software products, IP licensing, and digital innovation contracts.',
    icon: <LuGlobe size={24} />,
    accentColor: '#6366f1'
  }
};

const FALLBACK_THEME = {
  themeClass: 'card-prophecy',
  badge: 'Operating Entity',
  description: 'Manage commercial master service agreements, purchase orders, and legal documents.',
  icon: <LuBriefcase size={24} />,
  accentColor: '#3b5bfd'
};

const INITIAL_ENTITIES = [
  {
    id: 4,
    code: '25642730',
    name: 'Prophecy Consulting Inc',
    fullName: 'Prophecy Consulting Inc',
    themeClass: 'card-prophecy',
    badge: 'Primary US Entity',
    description: 'Direct client master service agreements, primary staffing agency MSAs, and prime client purchase orders.',
    icon: <LuShieldCheck size={24} />,
    accentColor: '#3b5bfd'
  },
  {
    id: 5,
    code: '25642740',
    name: 'ProphecyTek Pvt Ltd',
    fullName: 'ProphecyTek Pvt Ltd',
    themeClass: 'card-offshore',
    badge: 'Offshore Delivery',
    description: 'Global offshore delivery centers, cross-border development master agreements, and subcontractor work orders.',
    icon: <LuBuilding2 size={24} />,
    accentColor: '#0284c7'
  },
  {
    id: 6,
    code: '5785467',
    name: 'Cognifyar Technologies',
    fullName: 'Cognifyar Technologies',
    themeClass: 'card-cognifyar',
    badge: 'Technology Entity',
    description: 'Specialized technology agreements, software products, IP licensing, and digital innovation contracts.',
    icon: <LuGlobe size={24} />,
    accentColor: '#6366f1'
  }
];

const MsaPoEntitySelection = () => {
  const navigate = useNavigate();
  const [entities, setEntities] = useState(INITIAL_ENTITIES);
  const [entityStats, setEntityStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchEntitiesAndStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1. Fetch live companies from Accounting / Companies API
        let activeEntities = INITIAL_ENTITIES;
        try {
          const compRes = await axios.get(`${BASE_URL}/api/companies`, { headers });
          const compList = Array.isArray(compRes.data) ? compRes.data : (compRes.data?.data || []);
          if (Array.isArray(compList) && compList.length > 0) {
            activeEntities = compList.map((comp) => {
              const compId = Number(comp.Id || comp.id);
              const theme = DEFAULT_THEMES[String(compId)] || FALLBACK_THEME;
              const entityName = comp.Name || comp.name || '';
              const entityCode = comp.ClientId || comp.clientId || '';
              return {
                id: compId,
                code: entityCode,
                name: entityName,
                fullName: entityName,
                address: comp.Address || comp.address || '',
                themeClass: theme.themeClass,
                badge: theme.badge,
                description: theme.description,
                icon: theme.icon,
                accentColor: theme.accentColor
              };
            });
            setEntities(activeEntities);
          }
        } catch (compErr) {
          console.warn('Could not load live companies for MSA/PO workspace cards:', compErr.message);
        }

        // 2. Fetch counts for each entity in parallel
        const promises = activeEntities.map(async (entity) => {
          try {
            const [msaRes, poRes, sowRes] = await Promise.all([
              axios.get(`${BASE_URL}/api/crm/commercial-documents?governingEntityId=${entity.id}&typeCode=MSA&pageSize=1`, { headers }),
              axios.get(`${BASE_URL}/api/crm/commercial-documents?governingEntityId=${entity.id}&typeCode=PO&pageSize=1`, { headers }),
              axios.get(`${BASE_URL}/api/crm/commercial-documents?governingEntityId=${entity.id}&typeCode=SOW&pageSize=1`, { headers })
            ]);
            return {
              entityId: entity.id,
              msaCount: msaRes.data?.total ?? (entity.id === 4 ? 3 : 0),
              poCount: poRes.data?.total ?? 0,
              sowCount: sowRes.data?.total ?? 0
            };
          } catch {
            return {
              entityId: entity.id,
              msaCount: entity.id === 4 ? 3 : 0,
              poCount: 0,
              sowCount: 0
            };
          }
        });

        const results = await Promise.all(promises);
        const statsMap = {};
        results.forEach(r => {
          statsMap[r.entityId] = r;
        });
        setEntityStats(statsMap);
      } catch (err) {
        console.error('Error fetching entity agreement stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchEntitiesAndStats();
  }, []);

  const handleSelectEntity = (entityId) => {
    navigate(`/crm/msa-po/${entityId}`);
  };

  return (
    <div className="msa-po-container">
      {/* Breadcrumb Navigation */}
      <div className="msa-po-breadcrumbs">
        <span>CRM</span>
        <span>/</span>
        <span className="current">MSA & PO Workspace</span>
      </div>

      {/* Header */}
      <div className="msa-po-header">
        <div className="msa-po-title-area">
          <h1>
            <LuFileText size={26} color="#3b5bfd" />
            MSA & Purchase Orders
          </h1>
          <p>Select an entity workspace to manage Master Service Agreements, Purchase Orders, and Contract Templates.</p>
        </div>
      </div>

      {/* Entity Selection Cards */}
      <div className="msa-entity-grid">
        {entities.map((entity) => {
          const stats = entityStats[entity.id] || { msaCount: entity.id === 4 ? 3 : 0, poCount: 0 };
          return (
            <div
              key={entity.id}
              className={`msa-entity-card ${entity.themeClass}`}
              onClick={() => handleSelectEntity(entity.id)}
            >
              <div className="msa-entity-card-top">
                <div className="msa-entity-icon-box">
                  {entity.icon}
                </div>
                <span className="msa-entity-badge">
                  {entity.badge}
                </span>
              </div>

              <div className="msa-entity-name">
                {entity.name}
              </div>
              <div className="msa-entity-code">
                Entity Code: {entity.code}
              </div>

              <div className="msa-entity-desc">
                {entity.description}
              </div>

              <div className="msa-entity-stats-row">
                <div className="msa-entity-stat">
                  <div className="msa-entity-stat-val">
                    {loadingStats ? '—' : stats.msaCount}
                  </div>
                  <div className="msa-entity-stat-lbl">MSAs</div>
                </div>
                <div className="msa-entity-stat-divider" />
                <div className="msa-entity-stat">
                  <div className="msa-entity-stat-val">
                    {loadingStats ? '—' : stats.poCount}
                  </div>
                  <div className="msa-entity-stat-lbl">POs</div>
                </div>
                <div className="msa-entity-stat-divider" />
                <div className="msa-entity-stat">
                  <div className="msa-entity-stat-val">
                    {loadingStats ? '—' : (stats.sowCount ?? 0)}
                  </div>
                  <div className="msa-entity-stat-lbl">SOWs</div>
                </div>
              </div>

              <button
                type="button"
                className="msa-entity-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectEntity(entity.id);
                }}
              >
                <span>Open Entity Workspace</span>
                <LuArrowRight size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MsaPoEntitySelection;
